import React from "react"
import { cond, T } from "ramda"
import { useQuery } from "@apollo/client"
import { withAuthProvider } from "../../../lib/auth"
import QuickLinks from "../QuickLinks"
import { Side } from "../Panel"
import { NewMSRCreditsBrief, NewCompactPDRBrief } from "../PDRCredits"
import {
  isMember,
  isAssociate,
  isAffiliate,
  isPendingAssociate,
  isAffiliateToAssociatePending,
  isStudent,
  hasReciprocityAttribute,
  hasStartedReciprocityLevels,
  isB25Class,
  hasProspectDashboard,
  isProspectPath,
} from "../../utils"
import CourseCurriculum, { Loading } from "../CourseCurriculum/CourseCurriculum"
import PathBox from "../Prospect/PathBox"
import { MemberApplyPanel } from "../MemberApplyEntry"
import { useReplacePathWithApply } from "../MemberApplyEntry/MemberApplyPathContext"
import {
  ReciprocityApplicationPanel,
  useReciprocityApplicationLoading,
  useReciprocityApplicationState,
} from "../ReciprocityApplication"
import { withProspectDateQuery } from "../../../utils/prospect/withProspectDateQuery"
import { useMembershipJourney } from "../../../hooks/useMembershipJourney"
import { usePgmGraduate } from "../../../hooks/useMemberApplicationEligibility"
import { useMemberApplyPath } from "../../../hooks/useMemberApplyPath"
import { shouldRoutePendingAffiliate } from "../../utils"
import { MembershipJourneyProvider } from "../MembershipJourney/MembershipJourneyContext"
import { MEMBER_APPLICATION_ELIGIBILITY_QUERY } from "../../queries/memberApplicationEligibility"
import { COACH_TOOLS_API } from "../../../apolloClient"
import { PTM_MEMBERSHIP_APPLICATIONS_QUERY } from "../../../utils/prospect/withPtmApplicationsQuery"
import { shouldShowCourseCurriculumAdditionalRequirements } from "../MembershipJourney/utils/shouldShowCourseCurriculumAdditionalRequirements"

export const CourseCurriculumBriefing = ({ collapsed, person }) => {
  const { pgmGraduate } = usePgmGraduate({
    skip: !person || !isStudent(person),
  })
  // Eligibility and the Member application list feed the checklist, Apply, and
  // submitted-status CTA. Skip both whenever that surface cannot show:
  // collapsed briefing, qualifying associates, qualifying students (mid-program
  // past QL and graduates load it), and registered Affiliates.
  const skipEligibilityQuery =
    !person ||
    collapsed ||
    !shouldShowCourseCurriculumAdditionalRequirements({
      me: person,
      pgmGraduate,
    })
  const { data: eligibilityData, loading: eligibilityQueryLoading } = useQuery(
    MEMBER_APPLICATION_ELIGIBILITY_QUERY,
    {
      context: { api: COACH_TOOLS_API },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      skip: skipEligibilityQuery,
    },
  )

  const { eligibilityPath: memberApplyEligibilityPath, loading: pathLoading } =
    useMemberApplyPath({ skip: skipEligibilityQuery })
  // Same skip as the checklist: a submitted Member application replaces that
  // checklist, so the list has to be in hand before either button paints.
  const {
    data: memberApplicationsData,
    loading: memberApplicationsQueryLoading,
    error: memberApplicationsError,
  } = useQuery(PTM_MEMBERSHIP_APPLICATIONS_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    errorPolicy: "all",
    skip: skipEligibilityQuery,
  })
  const memberApplicationsList = memberApplicationsData?.membershipApplications

  if (collapsed) {
    return <div />
  }

  return (
    <MembershipJourneyProvider
      value={{
        me: person,
        pgaId: person?.pgaId,
        pgmProgress: person?.pgmProgress,
        pgmGraduate,
        memberApplicationEligibility:
          eligibilityData?.memberApplicationEligibility,
        // cache-and-network sets loading=true on background refetches; only
        // report the initial load so consumers don't flicker on refetch.
        memberApplicationEligibilityLoading:
          eligibilityQueryLoading && !eligibilityData,
        memberApplyEligibilityPath,
        memberApplyPathLoading: pathLoading,
        memberApplications: memberApplicationsList,
        memberApplicationsLoading:
          memberApplicationsQueryLoading && !memberApplicationsData,
        // A network error with no list is not an empty list. Apply and the
        // checklist stay hidden until a real result arrives.
        memberApplicationsFailed:
          Boolean(memberApplicationsError) && memberApplicationsList == null,
      }}
    >
      <CourseCurriculum />
    </MembershipJourneyProvider>
  )
}

export const PdrWidget = ({ collapsed }) => {
  return collapsed ? <NewCompactPDRBrief /> : <NewMSRCreditsBrief />
}

// Ramda cond invokes handlers as plain functions; hook-using views must render
// as JSX elements so React owns hook state in a child fiber.
const renderCourseCurriculumBriefing = props => (
  <CourseCurriculumBriefing {...props} />
)

export const chooseAppropriateViewForUser = cond([
  [({ loading }) => loading, () => <Loading />],
  // An authorized reciprocity candidate has no Associate or Affiliate path to
  // choose, so the prospect dashboard offers the Member application instead.
  [
    ({ replacePathWithApply }) => replacePathWithApply,
    ({ collapsed }) => (collapsed ? <div /> : <MemberApplyPanel />),
  ],
  // Once a candidate has started an application, show its progress in the same
  // slot instead of the Associate or Affiliate chooser.
  [
    ({ reciprocityApplication }) => Boolean(reciprocityApplication),
    ({ collapsed, reciprocityApplication }) =>
      collapsed ? (
        <div />
      ) : (
        <ReciprocityApplicationPanel application={reciprocityApplication} />
      ),
  ],
  [({ routingMe }) => isMember(routingMe), props => <PdrWidget {...props} />],
  [({ routingMe }) => isB25Class(routingMe), props => <PdrWidget {...props} />],
  [({ routingMe }) => isAssociate(routingMe), renderCourseCurriculumBriefing],
  // Registered Affiliates may have mainProgramTypeCode AF (Coach Tools), which
  // isAssociate does not match — keep Course Curriculum for that shape.
  [({ routingMe }) => isAffiliate(routingMe), renderCourseCurriculumBriefing],
  [
    ({ routingMe }) => isAffiliateToAssociatePending(routingMe),
    renderCourseCurriculumBriefing,
  ],
  [
    ({ routingMe }) => isPendingAssociate(routingMe),
    ({ collapsed }) => <QuickLinks collapsed={collapsed} />,
  ],
  [({ routingMe }) => isStudent(routingMe), renderCourseCurriculumBriefing],
  // Only show Reciprocity if they have started Z-levels, otherwise let prospects see PathBox
  [
    ({ routingMe }) =>
      hasReciprocityAttribute(routingMe) &&
      hasStartedReciprocityLevels(routingMe),
    renderCourseCurriculumBriefing,
  ],
  [
    ({ routingMe, currentMember }) =>
      hasProspectDashboard(routingMe) ||
      shouldRoutePendingAffiliate(routingMe) ||
      isProspectPath(currentMember),
    ({ collapsed }) => (collapsed ? <div /> : <PathBox />),
  ],
  [T, ({ collapsed }) => <QuickLinks collapsed={collapsed} />],
])

export const MembershipSailingView = ({
  me,
  authReady,
  collapsed,
  currentMember,
}) => {
  const { person, loading: personLoading } = useMembershipJourney()
  const routingMe = person || me
  const replacePathWithApply = useReplacePathWithApply()
  const reciprocityApplication = useReciprocityApplicationState()
  const reciprocityLoading = useReciprocityApplicationLoading()

  return (
    <Side basis={!collapsed && 55}>
      {chooseAppropriateViewForUser({
        me,
        person: routingMe,
        routingMe,
        loading: !authReady || (personLoading && !person) || reciprocityLoading,
        collapsed,
        currentMember,
        replacePathWithApply,
        reciprocityApplication,
      })}
    </Side>
  )
}

export default withProspectDateQuery(withAuthProvider(MembershipSailingView))
